import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router';

import { EVINCED_SEVERITIES, EvSection, EvSpinner } from '@evinced-private/ui-common';

import {
	MFA_SESSION_BREAKDOWN_BY_SEVERITY_PIE_CHART_DAID,
	MFA_SESSION_OVERVIEW_SUMMARY_DAID,
	MFA_SESSION_OVERVIEW_TAB_DAID,
	MFA_SESSION_SCAN_SEVERITIES_GRAPH_DAID,
	MFA_SESSION_TOP_ISSUE_TYPES_GRAPH_DAID,
	MFA_TOTAL_ISSUES_BREAKDOWN_BY_ISSUE_TYPE_CHART_DAID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import { useMfaSessionIssueTypesQuery } from 'src/apps/mfa/hooks/useMfaSessionIssueTypesQuery';
import { useMfaSessionSeveritiesQuery } from 'src/apps/mfa/hooks/useMfaSessionSeveritiesQuery';
import { useMfaSessionSummaryQuery } from 'src/apps/mfa/hooks/useMfaSessionSummaryQuery';
import { TMfaSessionIssuesFilterOptions } from 'src/apps/mfa/services/MfaDashboardServiceTypes';
import { createMobileSummaryItems } from 'src/apps/mobile-common/helpers/MobileSummaryPaneHelper';
import { MobileBreakdownBySeverityPieChart } from 'src/common/components/mobile-breakdown-by-severity-chart/MobileBreakdownBySeverityPieChart';
import { MobileScansImpactedBySeverityChart } from 'src/common/components/mobile-scans-impacted-by-severity-chart/MobileScansImpactedBySeverityChart';
import { MobileSummaryPane } from 'src/common/components/mobile-summary-pane/MobileSummaryPane';
import { MobileTopIssueTypesBarChart } from 'src/common/components/mobile-top-issue-types-bar-chart/MobileTopIssueTypesBarChart';
import { MobileTotalIssuesBreakdownByIssueTypeChart } from 'src/common/components/mobile-total-issues-breakdown-by-issue-type-chart/MobileTotalIssuesBreakdownByIssueTypeChart';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { MOBILE_SEVERITY_MAP } from 'src/common/helpers/MobileQueriesHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { MFA_SESSION_ALL_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { SessionViews } from 'src/common/types/WfaSessionViews';

import './MfaSessionOverview.scss';

interface IMfaSessionOverviewProps {
	sessionId: string;
}

export const MfaSessionOverview: FC<IMfaSessionOverviewProps> = ({ sessionId }) => {
	const history = useHistory();
	const { onFilterChange } = useTableState();

	const severitiesQuery = useMfaSessionSeveritiesQuery({
		sessionId
	});
	const sessionSummaryQuery = useMfaSessionSummaryQuery({
		sessionId
	});
	const issueTypesQuery = useMfaSessionIssueTypesQuery({
		sessionId
	});

	const summaryItems = useMemo(() => {
		if (!sessionSummaryQuery.data) {
			return [];
		}

		return createMobileSummaryItems(sessionSummaryQuery.data, ProductType.MOBILE_FLOW_ANALYZER);
	}, [sessionSummaryQuery.data]);

	const issuesTableIdForSession = useMemo(
		() => buildFullTableId(MFA_SESSION_ALL_ISSUES_TABLE_ID, sessionId),
		[sessionId]
	);

	const onChartClick = (filters: IFilterOptions): void => {
		onFilterChange(issuesTableIdForSession, filters);
		history.push(RoutesHelper.getMfaSessionPath(sessionId, SessionViews.ALL_ISSUES), {
			from: history.location.pathname
		});
	};

	const handleSeverityChartClick = (severity: EVINCED_SEVERITIES): void => {
		onChartClick({ severities: [{ value: severity, label: MOBILE_SEVERITY_MAP[severity] }] });
	};

	const handleTopIssueTypesBarChartClick = (issueType: string): void => {
		onChartClick({ types: [{ value: issueType, label: issueType }] });
	};

	const handleBreakdownByIssueTypeBarClick = (
		issueType: string,
		severity: EVINCED_SEVERITIES
	): void => {
		const filterOptions: TMfaSessionIssuesFilterOptions = {
			types: [{ value: issueType, label: issueType }],
			severities: [{ value: severity, label: MOBILE_SEVERITY_MAP[severity] }]
		};
		onChartClick(filterOptions);
	};

	return (
		<div className="mfa-session-overview">
			<EvSection className="mobile-summary-pane" dataAutomationId={MFA_SESSION_OVERVIEW_TAB_DAID}>
				{!sessionSummaryQuery.isLoading ? (
					<MobileSummaryPane
						sectionLabel="Scan summary"
						dataAutomationId={MFA_SESSION_OVERVIEW_SUMMARY_DAID}
						items={summaryItems}
					/>
				) : (
					<EvSpinner />
				)}
			</EvSection>
			<EvSection
				dataAutomationId={MFA_TOTAL_ISSUES_BREAKDOWN_BY_ISSUE_TYPE_CHART_DAID}
				skipLinkId="issue-types-chart-id"
			>
				{issueTypesQuery.data && !issueTypesQuery.isLoading ? (
					<MobileTotalIssuesBreakdownByIssueTypeChart
						data={issueTypesQuery.data}
						onBarClick={handleBreakdownByIssueTypeBarClick}
					/>
				) : (
					<EvSpinner />
				)}
			</EvSection>
			<div className="charts-container">
				<div className="charts-row">
					<EvSection
						className="pie-chart-section"
						dataAutomationId={MFA_SESSION_BREAKDOWN_BY_SEVERITY_PIE_CHART_DAID}
						skipLinkId="breakdown-pie-chart-id"
					>
						{severitiesQuery.data && !severitiesQuery.isLoading ? (
							<MobileBreakdownBySeverityPieChart
								severitiesCount={severitiesQuery.data}
								onPieClick={handleSeverityChartClick}
							/>
						) : (
							<EvSpinner />
						)}
					</EvSection>
					<EvSection
						dataAutomationId={MFA_SESSION_TOP_ISSUE_TYPES_GRAPH_DAID}
						skipLinkId="top-issue-types-chart-id"
					>
						{issueTypesQuery.data && !issueTypesQuery.isLoading ? (
							<MobileTopIssueTypesBarChart
								data={issueTypesQuery.data}
								onBarClick={handleTopIssueTypesBarChartClick}
							/>
						) : (
							<EvSpinner />
						)}
					</EvSection>
				</div>
				<div className="charts-row">
					<EvSection
						dataAutomationId={MFA_SESSION_SCAN_SEVERITIES_GRAPH_DAID}
						skipLinkId="severities-chart-id"
					>
						{severitiesQuery.data && !severitiesQuery.isLoading ? (
							<MobileScansImpactedBySeverityChart
								severitiesCount={severitiesQuery.data}
								onBarClick={handleSeverityChartClick}
							/>
						) : (
							<EvSpinner />
						)}
					</EvSection>
				</div>
			</div>
		</div>
	);
};
